import { useMutation, UseMutationResult } from '@tanstack/react-query';

import {
  DesiredSerialParentData,
  DesiredSerialParentDataResponse,
  LinkShipmentSearchData,
  LinkShipmentSearchResult,
  LiveSearchResponse,
  LocationNodeData,
  PartOrPartTypeSearchData,
  PartSearchData,
  PartTypeActionLiveSearchItem,
  SearchData,
  ShipmentSearchData,
  StructureMergeLocationNodeData,
  StructureMergeSearchData,
  SubLocationSearch,
} from '../@types';
import {
  AdvancePartSearch,
  BatchRecieveBulkLiveSearchData,
  PartLiveSearchData,
  PartTypeBulkLiveSearchDataInsertionType,
  PartTypeLiveSearchData,
} from '../@types/part-type.type';
import { SearchNodeParts } from '../@types/part.type';
import { ServiceData } from '../@types/service-search.type';
import {
  RecieveShipmentSearchNodes,
  SerializedShipmentManifestData,
} from '../@types/shipment.type';
import {
  advanceBulkSearchPartTypes,
  advanceBulkSearchSerials,
  advancePartSearch,
  advancePartSearchByName,
  advanceSearchPartTypes,
  advanceSearchPartTypesForBulkPartUpdate,
  batchRecieveBulkSearchSerials,
  getSearchShipment,
  getSubLocationsForCreateServices,
  getSubLocationsForUpdateServices,
  reciveShipmentsSearchNodes,
  replenishmentPartTypeSearch,
  replenishmentSearchNodes,
  replenishmentSkuSearch,
  searchAllNodesByName,
  searchAllocationNodes,
  searchChildPartTypesByClientAndContract,
  searchClientContractPartTypes,
  searchDesiredParentsForSerial,
  searchDispatchParts,
  searchLinkShipments,
  searchManufacturerNodes,
  searchManufacturerNodesByClientAndContract,
  searchNode,
  searchNodeByNameOrCode,
  searchNodeByParentId,
  searchNodeDispatch,
  searchNodeOperational,
  searchNodeParts,
  searchNodeReceive,
  searchNodeShipment,
  searchNodesPartsWithoutPartId,
  searchNodesParts,
  searchParts,
  searchPartsBulkUpdate,
  searchPartsOrPartTypes,
  searchPartsWithPid,
  searchPartTypesByClientAndContractForSKU,
  searchReturnPageDestination,
  searchReturnPageSource,
  searchService,
  searchNodeInStructureMerge,
  searchTlcNodesParts,
} from '../api/live-search';

// Interface for search node by parent ID data
interface SearchNodeByParentIdData {
  name: string;
  selectedParentId?: string | number;
  hasCode?: boolean;
  includeParentNode?: boolean;
  includeSiblings?: boolean;
  excludeReferenceSearch?: boolean;
}

// Interface for manufacturer search
interface ManufacturerSearchData {
  name: string;
  applicableNodeIds: string | number[];
}

// Interface for node parts search
interface NodePartsSearchData {
  id: number;
  name: string;
  nodeIdTree: string;
  includeSiblings: boolean;
  applicableNodeIds?: string | number[];
}

// Interface for child part types search
interface ChildPartTypesSearchData {
  name: string;
  applicableNodeIds: string | number[];
  categoryCode?: string;
}

// Interface for part type search by name
interface PartTypeSearchByNameData {
  name: string;
  applicableNodeIds?: string;
  nodeId?: number;
}

// Interface for dispatch parts search
interface DispatchPartsSearchData {
  name: string;
  applicableNodeIds?: string;
}

// Interface for TLC nodes parts search
interface TlcNodesPartsSearchData {
  id?: number;
  name: string;
  currentLocation: number;
  currentLocationType?: string;
}

export const useSubLocationsForCreateServices = (
  isNestedCheck?: boolean
): UseMutationResult<LocationNodeData[], Error, number> => {
  return useMutation({
    mutationFn: (data: number) => getSubLocationsForCreateServices(data, isNestedCheck),
  });
};

export const useSearchNode = (): UseMutationResult<LocationNodeData[], Error, string> => {
  return useMutation({
    mutationFn: (data: string) => searchNode(data),
  });
};

export const useSearchNodeByParentId = (): UseMutationResult<
  LocationNodeData[],
  Error,
  SearchNodeByParentIdData
> => {
  return useMutation({
    mutationFn: (data: SearchNodeByParentIdData) => searchNodeByParentId(data),
  });
};

export const useSearchService = (): UseMutationResult<ServiceData[], Error, number | string> => {
  return useMutation({
    mutationFn: (id: number | string) => searchService(id),
  });
};

export const useSearchManufacturerNodes = (): UseMutationResult<
  LocationNodeData[],
  Error,
  string
> => {
  return useMutation({
    mutationFn: (name: string) => searchManufacturerNodes(name),
  });
};

export const useSearchNodeOperational = (): UseMutationResult<
  LocationNodeData[],
  Error,
  SearchData
> => {
  return useMutation({
    mutationFn: (data: SearchData) => searchNodeOperational(data.name, data.hasCode),
  });
};

export const useSearchManufacturerNodesByClientAndContract = (): UseMutationResult<
  LocationNodeData[],
  Error,
  ManufacturerSearchData
> => {
  return useMutation({
    mutationFn: (data: ManufacturerSearchData) => searchManufacturerNodesByClientAndContract(data),
  });
};

export const useSearchNodesParts = (): UseMutationResult<
  LocationNodeData[],
  Error,
  NodePartsSearchData
> => {
  return useMutation({
    mutationFn: (data: NodePartsSearchData) => searchNodesParts(data),
  });
};

export const useSearchChildPartTypesByClientAndContract = (
  isSerialized?: boolean
): UseMutationResult<LocationNodeData[], Error, ChildPartTypesSearchData> => {
  return useMutation({
    mutationFn: (data: ChildPartTypesSearchData) =>
      searchChildPartTypesByClientAndContract(data, isSerialized),
  });
};

export const useSearchPartTypesByClientAndContractForSKU = (
  isSerialized?: boolean
): UseMutationResult<LocationNodeData[], Error, ManufacturerSearchData> => {
  return useMutation({
    mutationFn: (data: ManufacturerSearchData) =>
      searchPartTypesByClientAndContractForSKU(data, isSerialized),
  });
};

export const useSearchClientContractPartTypes = (): UseMutationResult<
  LocationNodeData[],
  Error,
  ManufacturerSearchData
> => {
  return useMutation({
    mutationFn: (data: ManufacturerSearchData) => searchClientContractPartTypes(data),
  });
};

export const useSearchParts = (
  searchLimit?: number
): UseMutationResult<LocationNodeData[], Error, SearchData> => {
  return useMutation({
    mutationFn: (data: SearchData) => searchParts(data, searchLimit),
  });
};

export const useReplenishmentSearchNodesSearchNodes = (): UseMutationResult<
  LocationNodeData[],
  Error,
  SearchData
> => {
  return useMutation({
    mutationFn: (data: SearchData) => replenishmentSearchNodes(data),
  });
};

export const useReplenishmentSkuSearch = (): UseMutationResult<
  LocationNodeData[],
  Error,
  PartTypeSearchByNameData
> => {
  return useMutation({
    mutationFn: (data: PartTypeSearchByNameData) => replenishmentSkuSearch(data),
  });
};

export const useReplenishmentPartTypeSearch = (): UseMutationResult<
  LocationNodeData[],
  Error,
  PartTypeSearchByNameData
> => {
  return useMutation({
    mutationFn: (data: PartTypeSearchByNameData) => replenishmentPartTypeSearch(data),
  });
};

export const useSearchNodeShipment = (): UseMutationResult<LocationNodeData[], Error, string> => {
  return useMutation({
    mutationFn: (data: string) => searchNodeShipment(data),
  });
};

export const useAdvanceSearchPartTypes = (): UseMutationResult<
  PartTypeLiveSearchData[],
  Error,
  PartTypeBulkLiveSearchDataInsertionType
> => {
  return useMutation({
    mutationFn: (data: PartTypeBulkLiveSearchDataInsertionType) => advanceSearchPartTypes(data),
  });
};

export const useAdvanceSearchPartTypesForBulkPartUpdate = (): UseMutationResult<
  PartTypeLiveSearchData[],
  Error,
  PartTypeBulkLiveSearchDataInsertionType
> => {
  return useMutation({
    mutationFn: (data: PartTypeBulkLiveSearchDataInsertionType) =>
      advanceSearchPartTypesForBulkPartUpdate(data),
  });
};

export const useAdvanceBulkSearchPartTypes = (): UseMutationResult<
  PartTypeLiveSearchData[],
  Error,
  PartTypeBulkLiveSearchDataInsertionType
> => {
  return useMutation({
    mutationFn: (data: PartTypeBulkLiveSearchDataInsertionType) =>
      advanceBulkSearchPartTypes(data),
  });
};

export const useSearchNodeDispatch = (): UseMutationResult<
  LocationNodeData[],
  Error,
  SearchData
> => {
  return useMutation({
    mutationFn: (data: SearchData) => searchNodeDispatch(data),
  });
};

export const useSearchPartsBulkUpdate = (): UseMutationResult<
  LocationNodeData[],
  Error,
  SearchData
> => {
  return useMutation({
    mutationFn: (data: SearchData) => searchPartsBulkUpdate(data),
  });
};

export const useSearchPartsWithPid = (): UseMutationResult<
  LocationNodeData[],
  Error,
  SearchData
> => {
  return useMutation({
    mutationFn: (data: SearchData) => searchPartsWithPid(data),
  });
};

export const useSearchPartsOrPartTypes = (): UseMutationResult<
  PartTypeActionLiveSearchItem[],
  Error,
  PartOrPartTypeSearchData
> => {
  return useMutation({
    mutationFn: (data: PartOrPartTypeSearchData) => searchPartsOrPartTypes(data),
  });
};

export const useSearchPartsByName = (): UseMutationResult<
  PartTypeLiveSearchData[],
  Error,
  string
> => {
  return useMutation({
    mutationFn: (data: string) => advancePartSearchByName(data),
  });
};

export const useSearchReturnPageSource = (
  isLiveSearch?: boolean
): UseMutationResult<LocationNodeData[], Error, string> => {
  return useMutation({
    mutationFn: (data: string) => searchReturnPageSource(data, isLiveSearch),
  });
};

export const useSearchReturnPageDestination = (
  isLiveSearch?: boolean
): UseMutationResult<LocationNodeData[], Error, string> => {
  return useMutation({
    mutationFn: (data: string) => searchReturnPageDestination(data, isLiveSearch),
  });
};

export const useSearchNodeInStructureMerge = (): UseMutationResult<
  StructureMergeLocationNodeData[],
  Error,
  StructureMergeSearchData
> => {
  return useMutation({
    mutationFn: (data: StructureMergeSearchData) => searchNodeInStructureMerge(data),
  });
};

export const useSearchDispatchParts = (): UseMutationResult<
  PartSearchData[],
  Error,
  DispatchPartsSearchData
> => {
  return useMutation({
    mutationFn: (data: DispatchPartsSearchData) => searchDispatchParts(data),
  });
};

export const useAdvanceBulkSearchSerials = (): UseMutationResult<
  PartSearchData[],
  Error,
  PartTypeBulkLiveSearchDataInsertionType
> => {
  return useMutation({
    mutationFn: (data: PartTypeBulkLiveSearchDataInsertionType) =>
      advanceBulkSearchSerials(data),
  });
};

export const useGetSearchShipment = (): UseMutationResult<ShipmentSearchData[], Error, string> => {
  return useMutation({
    mutationFn: (data: string) => getSearchShipment(data),
  });
};

export const useSearchNodePartsWithoutPartId = (): UseMutationResult<
  LocationNodeData[],
  Error,
  string
> => {
  return useMutation({
    mutationFn: (data: string) => searchNodesPartsWithoutPartId(data),
  });
};

export const useSearchLinkShipments = (): UseMutationResult<
  LinkShipmentSearchResult[],
  Error,
  LinkShipmentSearchData
> => {
  return useMutation({
    mutationFn: (data: LinkShipmentSearchData) => searchLinkShipments(data),
  });
};

export const useAdvancePartSearch = (): UseMutationResult<
  PartLiveSearchData[],
  Error,
  AdvancePartSearch
> => {
  return useMutation({
    mutationFn: (data: AdvancePartSearch) => advancePartSearch(data),
  });
};

export const useSearchAllNodesByName = (): UseMutationResult<
  LiveSearchResponse[],
  Error,
  string
> => {
  return useMutation({
    mutationFn: (data: string) => searchAllNodesByName(data),
  });
};

export const useSearchAllocationNodes = (): UseMutationResult<
  LocationNodeData[],
  Error,
  string
> => {
  return useMutation({
    mutationFn: (data: string) => searchAllocationNodes(data),
  });
};

export const useSearchNodeWithParts = (): UseMutationResult<
  LocationNodeData[],
  Error,
  SearchNodeParts
> => {
  return useMutation({
    mutationFn: (data: SearchNodeParts) => searchNodeParts(data),
  });
};

export const useSubLocationsForUpdateServices = (): UseMutationResult<
  LocationNodeData[],
  Error,
  SubLocationSearch
> => {
  return useMutation({
    mutationFn: async (data: SubLocationSearch) => {
      if (data.id === undefined) {
        throw new Error('SubLocationSearch.id is required but was undefined');
      }
      return getSubLocationsForUpdateServices(data);
    },
  });
};

export const useSearchNodeByNameOrCode = (): UseMutationResult<
  LocationNodeData[],
  Error,
  string
> => {
  return useMutation({
    mutationFn: (data: string) => searchNodeByNameOrCode(data),
  });
};

export const useSearchTlcNodesParts = (): UseMutationResult<
  LocationNodeData[],
  Error,
  TlcNodesPartsSearchData
> => {
  return useMutation({
    mutationFn: (data: TlcNodesPartsSearchData) => searchTlcNodesParts(data),
  });
};

export const useReciveShipmentsSearchNodes = (): UseMutationResult<
  LocationNodeData[],
  Error,
  RecieveShipmentSearchNodes
> => {
  return useMutation({
    mutationFn: (data: RecieveShipmentSearchNodes) => reciveShipmentsSearchNodes(data),
  });
};

export const useSearchNodeReceive = (): UseMutationResult<
  LocationNodeData[],
  Error,
  SearchData
> => {
  return useMutation({
    mutationFn: (data: SearchData) => searchNodeReceive(data),
  });
};

export const useBatchRecieveBulkSearchSerials = (): UseMutationResult<
  SerializedShipmentManifestData[],
  Error,
  BatchRecieveBulkLiveSearchData
> => {
  return useMutation({
    mutationFn: (data: BatchRecieveBulkLiveSearchData) => batchRecieveBulkSearchSerials(data),
  });
};

export const useSearchDesiredParentsForSerial = (): UseMutationResult<
  DesiredSerialParentDataResponse[],
  Error,
  DesiredSerialParentData
> => {
  return useMutation({
    mutationFn: (data: DesiredSerialParentData) => searchDesiredParentsForSerial(data),
  });
};
